import { secp256k1 } from '@noble/curves/secp256k1';
import { TypedData, TypedDataDomain } from 'abitype';
import { Hex, hexToNumber, Signature, toHex } from 'viem';

import { createExchangeProxyEIP712Domain, OrderWithBigInt, serializeOrder } from './eip712_utils';

export interface EIP712TypedData {
  types: TypedData;
  domain: TypedDataDomain;
  message: {
    [key: string]: unknown;
  };
  primaryType: string;
}

export type SignatureExtended = Signature & {
  recoveryParam: number;
};

/*
 * NOTE: This file is mostly copied from @0x/protocol-utils
 */

/**
 * Valid signature types on the Exchange Proxy.
 */
export enum SignatureType {
  Illegal = 0,
  Invalid = 1,
  EIP712 = 2,
  EthSign = 3,
}

// NOTE: Code below was copied and adapted from orders in @0x/protocol-utils
const LIMIT_ORDER_STRUCT_NAME = 'LimitOrder';
const LIMIT_ORDER_STRUCT_ABI = [
  { type: 'address', name: 'makerToken' },
  { type: 'address', name: 'takerToken' },
  { type: 'uint128', name: 'makerAmount' },
  { type: 'uint128', name: 'takerAmount' },
  { type: 'uint128', name: 'takerTokenFeeAmount' },
  { type: 'address', name: 'maker' },
  { type: 'address', name: 'taker' },
  { type: 'address', name: 'sender' },
  { type: 'address', name: 'feeRecipient' },
  { type: 'bytes32', name: 'pool' },
  { type: 'uint64', name: 'expiry' },
  { type: 'uint256', name: 'salt' },
];

export function getEIP712TypedDataForLimitOrder(order: OrderWithBigInt): EIP712TypedData {
  return {
    types: {
      [LIMIT_ORDER_STRUCT_NAME]: LIMIT_ORDER_STRUCT_ABI,
    },
    domain: createExchangeProxyEIP712Domain(order.chainId, order.verifyingContract),
    primaryType: LIMIT_ORDER_STRUCT_NAME,
    message: serializeOrder(order) as unknown as { [key: string]: unknown },
  };
}

/**
 * Split a signature into its components
 * Used in place of ethers splitSignature (https://github.com/wagmi-dev/viem/discussions/458)
 * @param signatureHex - Hex string of the signature
 */
export function splitSignature(signatureHex: Hex): SignatureExtended {
  const { r, s } = secp256k1.Signature.fromCompact(signatureHex.slice(2, 130));
  const v = hexToNumber(`0x${signatureHex.slice(130)}`);

  return padSignature({ v: BigInt(v), r: toHex(r), s: toHex(s), recoveryParam: 1 - (v % 2) });
}

/**
 * Sometimes signatures are split without leading bytes on the `r` and/or `s` fields.
 *
 * Add them if they don't exist.
 */
function padSignature(signature: SignatureExtended): SignatureExtended {
  const hexLength = 64;

  const result = { ...signature };

  const hexExtractor = /^0(x|X)(?<hex>\w+)$/;
  const rMatch = signature.r.match(hexExtractor);
  const rHex = rMatch?.groups?.hex;
  if (rHex) {
    if (rHex.length !== hexLength) {
      result.r = `0x${rHex.padStart(hexLength, '0')}`;
    }
  }

  const sMatch = signature.s.match(hexExtractor);
  const sHex = sMatch?.groups?.hex;
  if (sHex) {
    if (sHex.length !== hexLength) {
      result.s = `0x${sHex.padStart(hexLength, '0')}`;
    }
  }
  return result;
}
